/**
 * OneStamp Web Dashboard — Phase 4 Part B root.
 *
 * Management-only (Brand Owner + Super Admin). Customer / cashier flows
 * live exclusively in the Expo mobile app; if they sign in here they
 * hit /mobile-only.
 *
 * Architecture invariants:
 *   1. Hydrate Zustand auth from localStorage BEFORE rendering routes.
 *   2. A single QueryClient lives at the root; the auth store binds to
 *      it so signOut() can call queryClient.clear() — cache isolation
 *      across sessions / brand contexts (Mandate 2).
 *   3. Role-based route guards: super_admin → /admin/*,
 *      brand_owner → /owner/*, everyone else → /mobile-only.
 *   4. Sonner Toaster mounted once at root; pages call toast() directly.
 */
import React, { useEffect, useMemo } from 'react';
import '@/App.css';
import {
  BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';

import { useAuthStore, bindQueryClient, bindNavigator, routeForRole } from '@/lib/auth';
import { IS_CUSTOMER_HOST } from '@/lib/hostnameGuard';

import LoginPage from '@/pages/LoginPage';
import MobileOnlyPage from '@/pages/MobileOnlyPage';
import NotFoundPage from '@/pages/NotFoundPage';

import OwnerLayout from '@/components/OwnerLayout';
import RoleGate from '@/components/RoleGate';
import AdminLayout from '@/components/AdminLayout';
import CustomerLayout from '@/components/CustomerLayout';
import { MERCHANT_PRO_ROLES, ROLES } from '@/lib/rbac';

// R-2 — Pre-built allowed-role arrays. Co-located here so each route
// guard reads as a self-explanatory data binding instead of an inline
// magic list. Source of truth is `lib/rbac.js`.
const BRAND_CONFIG_ROLES = [ROLES.BRAND_OWNER, ROLES.SUPER_ADMIN];
const NO_GAME_MASTER_ROLES = [
  ROLES.BRAND_OWNER, ROLES.CASHIER, ROLES.SUPER_ADMIN,
];

// Customer pages (PHASE W-1)
// A3 (2026-06-27) — OTP imports removed. The deprecated phone+OTP
// pages (`CustomerLoginPage`, `CustomerSignUpPage`) have been retired
// per operator spec; the hidden `/customer/login-phone` and
// `/customer/signup-phone` routes now 404.
import CustomerHomePage from '@/pages/customer/HomePage';
import CustomerBrandsPage from '@/pages/customer/BrandsPage';
import CustomerBrandDetailPage from '@/pages/customer/BrandDetailPage';
import CustomerCardsPage from '@/pages/customer/CardsPage';
import CustomerCardDetailPage from '@/pages/customer/CardDetailPage';
import CustomerHistoryPage from '@/pages/customer/HistoryPage';
import CustomerQRPage from '@/pages/customer/QRPage';
import CustomerProfilePage from '@/pages/customer/ProfilePage';
import CustomerPromotionsPage from '@/pages/customer/PromotionsPage';
import CustomerPromotionDetailPage from '@/pages/customer/PromotionDetailPage';
import CustomerRewardsPage from '@/pages/customer/RewardsPage';
import CustomerRewardDetailPage from '@/pages/customer/RewardDetailPage';
import CustomerNotificationsPage from '@/pages/customer/NotificationsPage';
import CustomerRedemptionsPage from '@/pages/customer/RedemptionsPage';
import CustomerRedemptionDetailPage from '@/pages/customer/RedemptionDetailPage';
import CustomerHelpPage from '@/pages/customer/HelpPage';
import CustomerTermsPage from '@/pages/customer/TermsPage';
import CustomerSettingsPage from '@/pages/customer/SettingsPage';
import CustomerAccountSettingsPage from '@/pages/customer/AccountSettingsPage';
import CustomerDeleteAccountPage from '@/pages/customer/DeleteAccountPage';
// Phase R3 — Receipt OCR Claim (customer web surface).
import CustomerClaimStampUploadPage from '@/pages/customer/ClaimStampUploadPage';
import CustomerClaimStampReviewPage from '@/pages/customer/ClaimStampReviewPage';
import CustomerClaimStampStatusPage from '@/pages/customer/ClaimStampStatusPage';
import CustomerClaimStampHistoryPage from '@/pages/customer/ClaimStampHistoryPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';

// Owner pages
import OwnerOverviewPage from '@/pages/owner/OverviewPage';
import OwnerAnalyticsPage from '@/pages/owner/AnalyticsPage';
import OwnerReservationsListPage from '@/pages/owner/ReservationsListPage';
import OwnerReservationDetailPage from '@/pages/owner/ReservationDetailPage';
import OwnerWalkInWaitlistPage from '@/pages/owner/WalkInWaitlistPage';
import OwnerManualBookingPage from '@/pages/owner/ManualBookingPage';
import OwnerLiveFloorPlanPage from '@/pages/owner/LiveFloorPlanPage';
import OwnerNotificationsPage from '@/pages/owner/NotificationsPage';
import OwnerBranchesPage from '@/pages/owner/BranchesPage';
import OwnerCashiersPage from '@/pages/owner/CashiersPage';
import OwnerCustomerCrmPage from '@/pages/owner/CustomerCrmPage';
import OwnerRetentionPage from '@/pages/owner/RetentionPage';
import OwnerRewardsPage from '@/pages/owner/RewardsPage';
import OwnerPromotionsPage from '@/pages/owner/PromotionsPage';
import OwnerBirthdayPromotionsPage from '@/pages/owner/BirthdayPromotionsPage';
import InvitePage from '@/pages/Invite';
import OwnerReviewsPage from '@/pages/owner/ReviewsPage';
import OwnerSettingsPage from '@/pages/owner/SettingsPage';
import OwnerAuditPage from '@/pages/owner/AuditPage';
import OwnerStaffRolesPage from '@/pages/owner/StaffRolesPage';
import OwnerStaffUsersPage from '@/pages/owner/StaffUsersPage';
// Phase R4 — Receipt OCR Claim verification (owner web surface).
import OwnerReceiptClaimsPage from '@/pages/owner/ReceiptClaimsPage';
import OwnerReceiptClaimDetailPage from '@/pages/owner/ReceiptClaimDetailPage';
// R10.4c (2026-07-05) — Outlet Mapping master.
import OwnerOutletMappingPage from '@/pages/owner/OutletMappingPage';

// Owner Membership pages (Pass B)
import MembershipLayout from '@/pages/owner/membership/MembershipLayout';
import OwnerMembershipDashboardPage from '@/pages/owner/membership/DashboardPage';
import OwnerMembershipTiersPage from '@/pages/owner/membership/TiersPage';
import OwnerMembershipPlansPage from '@/pages/owner/membership/PlansPage';
import OwnerMembershipBenefitsPage from '@/pages/owner/membership/BenefitsPage';
import OwnerMembershipMembersPage from '@/pages/owner/membership/MembersPage';

// Admin pages
import AdminOverviewPage from '@/pages/admin/OverviewPage';
import AdminBrandsPage from '@/pages/admin/BrandsPage';
import AdminBrandDrilldownPage from '@/pages/admin/BrandDrilldownPage';
import AdminBrandRetentionPage from '@/pages/admin/BrandRetentionPage';
import AdminBrandCustomerCrmPage from '@/pages/admin/BrandCustomerCrmPage';
import AdminOwnersPage from '@/pages/admin/OwnersPage';
import AdminCategoriesPage from '@/pages/admin/CategoriesPage';
import AdminAuditPage from '@/pages/admin/AuditPage';
import AdminEventFailuresPage from '@/pages/admin/EventFailuresPage';

// Phase 1 — staff login aliases (separated entry points). Each wraps the
// shared email+password LoginPage so deep-links land on a role-scoped URL
// without forking the form code.
import CashierLoginPage from '@/pages/staff/CashierLoginPage';
import OwnerLoginPage from '@/pages/staff/OwnerLoginPage';
import AdminLoginPage from '@/pages/staff/AdminLoginPage';

// Phase 1 — legacy email+password customer fallback. Routes NOT linked
// from the new phone+OTP UI; reachable only by direct URL so existing
// email customers (and demo/QA) can still authenticate.
import CustomerLoginEmailPage from '@/pages/customer/LoginEmailPage';
import CustomerSignUpEmailPage from '@/pages/customer/SignUpEmailPage';
// A3 (2026-06-27) — Complete Profile + Change Password gate screens.
import CustomerCompleteProfilePage from '@/pages/customer/CompleteProfilePage';
import CustomerChangePasswordPage from '@/pages/customer/ChangePasswordPage';
// 2026-06-28 — Emergent Managed Google hosted-redirect callback page.
import EmergentAuthCallbackPage from '@/pages/customer/EmergentAuthCallbackPage';

function BootLoader() {
  return (
    <div
      className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"
      data-testid="boot-loading"
    >
      <span className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/**
 * A3 (2026-06-27) — Bind react-router's imperative navigator into the
 * auth-store once the Router is mounted. Required for the global 403
 * interceptors + gated-router helpers in `lib/auth.js` which run from
 * outside React (fetch callbacks).
 */
function NavigatorBinder() {
  const navigate = useNavigate();
  useEffect(() => {
    bindNavigator(navigate);
  }, [navigate]);
  return null;
}

function RequireRole({ role, children }) {
  const { user, token, loading } = useAuthStore();
  const location = useLocation();
  if (loading) return <BootLoader />;
  if (!token || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (user.role !== role) {
    return <Navigate to={routeForRole(user.role)} replace />;
  }
  return children;
}

// PHASE W-1 — customer guard. Differs from RequireRole only in the
// fallback target: unauthenticated customers bounce to /customer/login
// (the dedicated customer entrypoint), and authenticated non-customers
// bounce to their own role surface — never leaking customer routes to
// staff-context sessions.
function RequireCustomer({ children }) {
  const { user, token, loading } = useAuthStore();
  const location = useLocation();
  if (loading) return <BootLoader />;
  if (!token || !user) {
    return <Navigate to="/customer/login" replace state={{ from: location.pathname }} />;
  }
  if (user.role !== 'customer') {
    return <Navigate to={routeForRole(user.role)} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, token, loading } = useAuthStore();
  if (loading) return <BootLoader />;
  if (!token || !user) {
    // Phase 1 — apex now defaults to customer login on every host
    // (the new customer phone+OTP page is the primary entrypoint).
    return <Navigate to="/customer/login" replace />;
  }
  return <Navigate to={routeForRole(user.role)} replace />;
}

/**
 * Customer-host block redirect. Renders a `Navigate` to an
 * appropriate customer destination — `/customer/home` if the user is
 * signed in, `/customer/login` otherwise. Used to gate every staff
 * route + bare `/login` + `/mobile-only` when running on
 * `my.onestamp.id`.
 */
function CustomerHostBlock() {
  const { user, token, loading } = useAuthStore();
  if (loading) return <BootLoader />;
  return (
    <Navigate
      to={token && user ? '/customer/home' : '/customer/login'}
      replace
    />
  );
}

function App() {
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            retry: 1,
            refetchOnWindowFocus: false,
            staleTime: 15_000,
          },
        },
      }),
    [],
  );

  const hydrate = useAuthStore((s) => s.hydrate);
  useEffect(() => {
    bindQueryClient(queryClient);
    hydrate();
  }, [queryClient, hydrate]);

  return (
    <QueryClientProvider client={queryClient}>
      <div className="App">
        <BrowserRouter>
          <NavigatorBinder />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route
              path="/login"
              element={IS_CUSTOMER_HOST ? <CustomerHostBlock /> : <LoginPage />}
            />
            {/* Phase 1 — dedicated staff login routes (alias the existing form). */}
            <Route path="/cashier/login" element={<CashierLoginPage />} />
            <Route path="/owner/login" element={<OwnerLoginPage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            {/* Phase 1 — hidden email fallback for legacy customers. Kept as the
                primary email+password surface post-A3. */}
            <Route path="/customer/login-email" element={<CustomerLoginEmailPage />} />
            <Route path="/customer/signup-email" element={<CustomerSignUpEmailPage />} />
            {/* A3 (2026-06-27) — retired phone+OTP routes. The deprecated
                `/api/auth/customer/*` backend endpoints remain live but the
                client-side pages have been deleted. These two routes now 404
                via the catch-all at the end of the <Routes>. */}
            <Route path="/invite" element={<InvitePage />} />
            <Route
              path="/mobile-only"
              element={IS_CUSTOMER_HOST ? <CustomerHostBlock /> : <MobileOnlyPage />}
            />

            {/* PHASE W-1 — Customer Web Shell */}
            {/* A3 (2026-06-27) — public /customer/login and /customer/signup
                render the identifier+password forms (email or phone, auto-
                detected) plus Google / Apple sign-in. */}
            <Route path="/customer/login" element={<CustomerLoginEmailPage />} />
            <Route path="/customer/signup" element={<CustomerSignUpEmailPage />} />
            {/* A3 (2026-06-27) — two blocking gate screens. Reached only by
                the auth-store's gated router (post-login / post-register /
                403-interceptor). They render for any authenticated user and
                enforce their own back-navigation lock, so no RoleGate wrap. */}
            <Route
              path="/customer/complete-profile"
              element={<CustomerCompleteProfilePage />}
            />
            <Route
              path="/customer/change-password"
              element={<CustomerChangePasswordPage />}
            />
            {/* 2026-06-28 — Emergent Managed Google hosted-redirect callback.
                Reads #session_id= from the URL fragment, exchanges via the
                A1 adapter endpoint, and hands the TokenResponse to the
                auth-store's gated router. */}
            <Route
              path="/customer/auth/callback"
              element={<EmergentAuthCallbackPage />}
            />
            <Route
              path="/customer"
              element={
                <RequireCustomer>
                  <CustomerLayout />
                </RequireCustomer>
              }
            >
              <Route index element={<Navigate to="/customer/home" replace />} />
              <Route path="home" element={<CustomerHomePage />} />
              <Route path="brands" element={<CustomerBrandsPage />} />
              <Route path="brand/:id" element={<CustomerBrandDetailPage />} />
              <Route path="cards" element={<CustomerCardsPage />} />
              <Route path="cards/:cardId" element={<CustomerCardDetailPage />} />
              <Route path="rewards" element={<CustomerRewardsPage />} />
              <Route path="reward/:id" element={<CustomerRewardDetailPage />} />
              <Route path="notifications" element={<CustomerNotificationsPage />} />
              <Route path="redemptions" element={<CustomerRedemptionsPage />} />
              <Route path="redemption/:id" element={<CustomerRedemptionDetailPage />} />
              <Route path="help" element={<CustomerHelpPage />} />
              <Route path="terms" element={<CustomerTermsPage />} />
              <Route path="history" element={<CustomerHistoryPage />} />
              <Route path="qr" element={<CustomerQRPage />} />
              <Route path="profile" element={<CustomerProfilePage />} />
              <Route path="settings" element={<CustomerSettingsPage />} />
              <Route path="settings/account" element={<CustomerAccountSettingsPage />} />
              <Route path="settings/account/delete" element={<CustomerDeleteAccountPage />} />
              <Route path="promotions" element={<CustomerPromotionsPage />} />
              <Route
                path="promotions/:id"
                element={<CustomerPromotionDetailPage />}
              />
              {/* Phase R3 — Receipt OCR Claim (customer flow). All
                  routes live under /customer so RequireCustomer guards
                  them + CustomerLayout keeps the bottom nav visible
                  on mobile and the sidebar on desktop. */}
              <Route
                path="claim-stamp/history"
                element={<CustomerClaimStampHistoryPage />}
              />
              <Route
                path="claim-stamp/review/:claimId"
                element={<CustomerClaimStampReviewPage />}
              />
              <Route
                path="claim-stamp/status/:claimId"
                element={<CustomerClaimStampStatusPage />}
              />
              <Route
                path="claim-stamp/:brandId"
                element={<CustomerClaimStampUploadPage />}
              />
            </Route>

            {/* R-2 (2026-06-18) — `/owner/*` outer guard widened from
                brand_owner-only to the operational super-set per
                MERCHANT_PRO_RBAC_R2_EXECUTION_BRIEF §5. Cashier + GM
                consume the SAME layout + 7 shared components as
                brand_owner; super_admin retains cross-brand reach.
                Per-route narrowings (e.g. GM denied on Manual
                Booking + CRM) are enforced via inner <RoleGate>
                wraps on the routes that need them. */}
            <Route
              path="/owner"
              element={
                IS_CUSTOMER_HOST ? (
                  <CustomerHostBlock />
                ) : (
                  <RoleGate allowedRoles={MERCHANT_PRO_ROLES}>
                    <OwnerLayout />
                  </RoleGate>
                )
              }
            >
              {IS_CUSTOMER_HOST ? null : (
                <>
                  <Route index element={<OwnerOverviewPage />} />
                  <Route
                    path="analytics"
                    element={
                      <RoleGate requiredCapability="analytics.view">
                        <OwnerAnalyticsPage />
                      </RoleGate>
                    }
                  />
                  <Route path="reservations" element={<OwnerReservationsListPage />} />
                  <Route path="reservations/:reservationId" element={<OwnerReservationDetailPage />} />
                  <Route path="waitlist" element={<OwnerWalkInWaitlistPage />} />
                  <Route path="floor-plan" element={<OwnerLiveFloorPlanPage />} />
                  <Route path="notifications" element={<OwnerNotificationsPage />} />
                  <Route
                    path="manual-booking"
                    element={
                      <RoleGate requiredCapability="manual_booking.commit">
                        <OwnerManualBookingPage />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="branches"
                    element={
                      <RoleGate requiredCapability="brand_config.manage_branches">
                        <OwnerBranchesPage />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="cashiers"
                    element={
                      <RoleGate requiredCapability="brand_config.manage_cashiers">
                        <OwnerCashiersPage />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="crm"
                    element={
                      <RoleGate requiredCapability="crm.view_list">
                        <OwnerCustomerCrmPage />
                      </RoleGate>
                    }
                  />
                  {/* Phase R4 (2026-06-29) — Receipt Claim
                      verification. Guarded by MERCHANT_PRO_ROLES at
                      the outer `/owner` route; brand_owner /
                      super_admin only (backend `require_roles(
                      'brand_owner', 'super_admin')` is the real
                      enforcement). */}
                  <Route path="receipt-claims" element={<OwnerReceiptClaimsPage />} />
                  <Route
                    path="receipt-claims/:claimId"
                    element={<OwnerReceiptClaimDetailPage />}
                  />
                  {/* Phase R10.4c (2026-07-05) — Owner Outlet Mapping.
                      Guarded by MERCHANT_PRO_ROLES at the outer `/owner`
                      route; backend router enforces the real RBAC. */}
                  <Route path="outlet-mapping" element={<OwnerOutletMappingPage />} />
                  <Route
                    path="retention"
                    element={
                      <RoleGate requiredCapability="brand_config.view_retention">
                        <OwnerRetentionPage />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="rewards"
                    element={
                      <RoleGate requiredCapability="brand_config.manage_rewards">
                        <OwnerRewardsPage />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="membership"
                    element={
                      <RoleGate requiredCapability="membership.manage_config">
                        <MembershipLayout />
                      </RoleGate>
                    }
                  >
                    <Route index element={<Navigate to="/owner/membership/dashboard" replace />} />
                    <Route path="dashboard" element={<OwnerMembershipDashboardPage />} />
                    <Route path="tiers" element={<OwnerMembershipTiersPage />} />
                    <Route path="plans" element={<OwnerMembershipPlansPage />} />
                    <Route path="benefits" element={<OwnerMembershipBenefitsPage />} />
                    <Route path="members" element={<OwnerMembershipMembersPage />} />
                  </Route>
                  <Route
                    path="promotions"
                    element={
                      <RoleGate requiredCapability="brand_config.manage_promotions">
                        <OwnerPromotionsPage />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="birthday-promotions"
                    element={
                      <RoleGate requiredCapability="brand_config.manage_promotions">
                        <OwnerBirthdayPromotionsPage />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="reviews"
                    element={
                      <RoleGate requiredCapability="brand_config.manage_reviews">
                        <OwnerReviewsPage />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="settings"
                    element={
                      <RoleGate requiredCapability="brand_config.manage_settings">
                        <OwnerSettingsPage />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="audit"
                    element={
                      <RoleGate requiredCapability="brand_config.view_audit">
                        <OwnerAuditPage />
                      </RoleGate>
                    }
                  />
                  {/* Sub-phase RBAC-C3 (2026-06-18) — staff management. */}
                  <Route
                    path="staff/roles"
                    element={
                      <RoleGate requiredCapability="staff.view">
                        <OwnerStaffRolesPage />
                      </RoleGate>
                    }
                  />
                  <Route
                    path="staff/users"
                    element={
                      <RoleGate requiredCapability="staff.view">
                        <OwnerStaffUsersPage />
                      </RoleGate>
                    }
                  />
                </>
              )}
            </Route>

            {/* Super Admin — same hostname gating. */}
            <Route
              path="/admin"
              element={
                IS_CUSTOMER_HOST ? (
                  <CustomerHostBlock />
                ) : (
                  <RequireRole role="super_admin">
                    <AdminLayout />
                  </RequireRole>
                )
              }
            >
              {IS_CUSTOMER_HOST ? null : (
                <>
                  <Route index element={<AdminOverviewPage />} />
                  <Route path="brands" element={<AdminBrandsPage />} />
                  <Route path="brands/:brand_id" element={<AdminBrandDrilldownPage />} />
                  <Route path="brands/:brand_id/retention" element={<AdminBrandRetentionPage />} />
                  <Route path="brands/:brand_id/crm" element={<AdminBrandCustomerCrmPage />} />
                  <Route path="owners" element={<AdminOwnersPage />} />
                  <Route path="categories" element={<AdminCategoriesPage />} />
                  <Route path="audit" element={<AdminAuditPage />} />
                  <Route path="event-failures" element={<AdminEventFailuresPage />} />
                </>
              )}
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster
          theme="dark"
          position="bottom-right"
          richColors
          toastOptions={{
            style: {
              background: '#1A1A1A',
              border: '1px solid rgba(255,255,255,0.08)',
              color: '#fff',
            },
          }}
        />
      </div>
    </QueryClientProvider>
  );
}

export default App;
